// App Bridge init + session-token retrieval.
//
// TRD: docs/product/features/completed/shopify-embedded-install-auth/trd.md
// Bounded Unit B, B1/B3.
//
// Package choice: `@shopify/app-bridge` (core, GA `latest` dist-tag
// 3.7.11 as of implementation) rather than `@shopify/app-bridge-react`
// (npm `latest` 4.2.12) -- v4 of the React package targets Shopify's newer
// script-tag-loaded "unified App Bridge" model (a `<script
// src="https://cdn.shopify.com/shopifycloud/app-bridge.js">` global, not an
// npm-bundled client), a materially different integration shape than what
// the TRD's package.json-dependency framing describes. `@shopify/app-bridge`
// v3's `createApp`/`getSessionToken` API is stable, well-documented, and
// matches the TRD's literal naming. FLAG: re-verify this choice against
// Shopify's current guidance before this ships -- the TRD explicitly called
// out that Shopify has renamed/restructured this package across major
// versions before, and the v4 "unified" model is where Shopify's docs are
// steering new integrations as of this writing.
import createApp from '@shopify/app-bridge';
import { getSessionToken as appBridgeGetSessionToken } from '@shopify/app-bridge/utilities';
import { Redirect } from '@shopify/app-bridge/actions';
import Config from '../Config';
import { getShopifyContext } from './embeddedContext';

let appBridgeInstance = null;

/**
 * Idempotent. Returns null (and does NOT throw) when called outside a
 * valid embedded context -- callers must not attempt App Bridge init for a
 * standalone page load (B1 acceptance criteria).
 */
export function initAppBridge() {
  if (appBridgeInstance) return appBridgeInstance;
  const ctx = getShopifyContext();
  if (!ctx || !ctx.host) return null;
  if (!Config.SHOPIFY_API_KEY) {
    // Missing build-time config -- fail loudly in the console (not to the
    // user, this is a deploy-config problem) rather than silently no-op,
    // which would otherwise look identical to "not embedded" and be much
    // harder to diagnose.
    console.error('[shopify] REACT_APP_SHOPIFY_API_KEY is not set; App Bridge cannot initialize.');
    return null;
  }
  appBridgeInstance = createApp({
    apiKey: Config.SHOPIFY_API_KEY,
    host: ctx.host,
    forceRedirect: true,
  });
  return appBridgeInstance;
}

export function getAppBridgeInstance() {
  return appBridgeInstance || initAppBridge();
}

/**
 * Always fetches a FRESH session token (60s lifetime -- TRD §0.3, B3).
 * Never cache/reuse the resolved value across calls. Rejects if App Bridge
 * isn't initialized (non-embedded context) or Shopify itself fails to
 * produce a token (e.g. revoked access).
 */
export function getShopifySessionToken() {
  const app = getAppBridgeInstance();
  if (!app) {
    return Promise.reject(new Error('Shopify App Bridge is not initialized (not an embedded context).'));
  }
  return appBridgeGetSessionToken(app);
}

// TRD: docs/product/features/completed/shopify-billing-integration/trd.md,
// Bounded Unit B (cq-app), Section 3. Navigates the TOP-LEVEL frame to a
// Shopify-hosted URL (the AppSubscription/AppUsageRecord confirmation
// screen) -- required because Shopify refuses to render that screen inside
// the app's own iframe. When embedded, uses App Bridge's Redirect action
// with Redirect.Action.REMOTE, which App Bridge implements by navigating
// `window.top` (breaking out of the iframe) rather than the iframe itself --
// this is the same App Bridge instance B1/B3 already initialize, no second
// App Bridge concept introduced. Outside an embedded context there is no
// iframe to escape, so a plain top-level navigation is already correct.
export function redirectTopLevel(url) {
  const app = getAppBridgeInstance();
  if (app) {
    const redirect = Redirect.create(app);
    redirect.dispatch(Redirect.Action.REMOTE, url);
    return;
  }
  window.location.href = url;
}
